import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Box, Button, Row, TextField } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import VideoPlayer from '~/components/VideoPlayer/VideoPlayer';
import { VideoUrlInput } from '~/components/VideoUrlInput/VideoUrlInput';
import { useStore } from '~/contexts';
import { useCreateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useCreateCustomPageTemplate';
import { useTemplateSaveErrorHandler } from '~/hooks/reactQuery/mutations/useTemplateSaveErrorHandler';
import { useUpdateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useUpdateCustomPageTemplate';
import { useCustomPageTemplates } from '~/hooks/reactQuery/queries/useCustomPageTemplates';
import { useLocaleKeys } from '~/i18n';
import { isDuplicateTemplateName } from '~/models/custom-page-template/isDuplicateTemplateName';
import { selectors } from '~/store/state-selectors';
import { isUrlValid } from '~/utils/videoUrl/videoUrl';
import s from './VideoTemplateFormModal.module.scss';
export const GetVideoTemplateFormModalTID = (parentTestId)=>getTestIds(parentTestId);
export const VideoTemplateForm = (param)=>{
    let { mode, template, onBack, onSaved } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetVideoTemplateFormModalTID(), []);
    const businessId = useStore(selectors.user.businessId);
    const { templates: existingTemplates } = useCustomPageTemplates();
    const [name, setName] = useState(mode === 'edit' && template ? template.name : '');
    const [pageTitle, setPageTitle] = useState(mode === 'edit' && template ? template.pageTitle : '');
    const [videoUrl, setVideoUrl] = useState(mode === 'edit' && template ? template.video.videoUrl : '');
    const [nameError, setNameError] = useState(undefined);
    const [pageTitleError, setPageTitleError] = useState(undefined);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const { createTemplate, isLoading: isCreateLoading } = useCreateCustomPageTemplate({
        onSuccess: ()=>{
            onSaved();
        }
    });
    const { updateTemplate, isLoading: isUpdateLoading } = useUpdateCustomPageTemplate({
        onSuccess: ()=>{
            onSaved();
        }
    });
    const handleSaveError = useTemplateSaveErrorHandler({
        mode,
        setNameError
    });
    const isLoading = isCreateLoading || isUpdateLoading;
    const handleSave = ()=>{
        if (isLoading) return;
        let hasError = false;
        if (name.trim() === '') {
            setNameError(t.customPages.templates.fieldRequired());
            hasError = true;
        } else if (isDuplicateTemplateName(name, existingTemplates, template === null || template === void 0 ? void 0 : template.id)) {
            setNameError(t.customPages.templates.duplicateNameMessage());
            hasError = true;
        } else {
            setNameError(undefined);
        }
        if (pageTitle.trim() === '') {
            setPageTitleError(t.customPages.templates.fieldRequired());
            hasError = true;
        } else {
            setPageTitleError(undefined);
        }
        if (videoUrl.trim() === '' || !isUrlValid(videoUrl)) {
            setSubmitAttempted(true);
            hasError = true;
        }
        if (hasError) return;
        const payload = {
            type: 'video',
            name: name.trim(),
            pageTitle: pageTitle.trim(),
            displayScope: 'proposalOnly',
            video: {
                videoUrl
            }
        };
        if (mode === 'create') {
            createTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                data: payload
            }, {
                onError: handleSaveError
            });
        } else if (template) {
            updateTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                templateId: template.id,
                data: payload
            }, {
                onError: handleSaveError
            });
        }
    };
    const handleNameChange = (e)=>{
        setName(e.target.value);
        if (nameError) {
            setNameError(undefined);
        }
    };
    const handlePageTitleChange = (e)=>{
        setPageTitle(e.target.value);
        if (pageTitleError) {
            setPageTitleError(undefined);
        }
    };
    const showVideoPreview = isUrlValid(videoUrl);
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.VideoTemplateFormModalTestIds.Root(),
        className: s.formContent,
        children: [
            /*#__PURE__*/ _jsx(TextField, {
                testId: testIds.VideoTemplateFormModalTestIds.NameField(),
                label: t.customPages.templates.nameLabel(),
                value: name,
                onChange: handleNameChange,
                error: nameError != null,
                helperText: nameError,
                bi: {
                    name: 'video_template_form_name'
                }
            }),
            /*#__PURE__*/ _jsx(TextField, {
                testId: testIds.VideoTemplateFormModalTestIds.PageTitleField(),
                label: t.customPages.templates.pageTitleLabel(),
                value: pageTitle,
                onChange: handlePageTitleChange,
                error: pageTitleError != null,
                helperText: pageTitleError,
                bi: {
                    name: 'video_template_form_page_title'
                }
            }),
            /*#__PURE__*/ _jsx(VideoUrlInput, {
                value: videoUrl,
                onChange: setVideoUrl,
                required: true,
                submitAttempted: submitAttempted,
                testId: testIds.VideoTemplateFormModalTestIds.VideoUrlInput()
            }),
            showVideoPreview && /*#__PURE__*/ _jsx(Box, {
                testId: testIds.VideoTemplateFormModalTestIds.VideoPreview(),
                className: s.videoPreview,
                children: /*#__PURE__*/ _jsx(VideoPlayer, {
                    url: videoUrl
                })
            }),
            /*#__PURE__*/ _jsxs(Row, {
                className: s.formFooter,
                justifyContent: "center",
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        onClick: onBack,
                        testId: testIds.VideoTemplateFormModalTestIds.CancelButton(),
                        bi: {
                            name: 'video_template_form_cancel'
                        },
                        children: t.customPages.templates.cancel()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primary",
                        loading: isLoading,
                        onClick: handleSave,
                        testId: testIds.VideoTemplateFormModalTestIds.SaveButton(),
                        bi: {
                            name: 'video_template_form_save'
                        },
                        children: t.customPages.templates.save()
                    })
                ]
            })
        ]
    });
};
